import { useReducerAsync } from 'use-reducer-async';
import { createContainer } from 'react-tracked';
import { currentUser } from '../services/authService';
import settings from './settings';
import { sendAmplitudeData, setAmplitudeUserId } from '../utils/amplitudeUtils';

const initialState = {
  pending: false,
  error: null,
  interviewerNotFound: false,
  user: null,
  interviews: null,
  interview: null,
  metadata: null,
  experienceMetadata: null,
  metadataError: false,
  candidate: null,
  interviewSubmitStatus: null,
  interviewSubmitValidation: null,
  pagination: { page: 1, count: null, totalPages: null },
  count: 0,
  interviewStatusFilter: 'All Interviews',
  interviewScheduled: false,
  interviewReportPending: false,
  interviewCompleted: false,
  missingVideo: false,
  querySearch: '',
  dateSearchStart: '',
  dateSearchEnd: '',
  jdPDF: { id: null, url: null },
  resumePDF: { id: null, url: null },
  interviewers: null,
  interviewerId: null,
  invalidInterview: false,
  interviewError: false,
  nonTechCheckbox: false,
  temporalLockInterviews: [],
  checklistItems: [],
};

const reducer = (state, action) => {
  switch (action.type) {
    case 'STARTED':
      return {
        ...state,
        pending: true,
        interviewerNotFound: false,
      };
    case 'USER_NOT_FOUND':
      return {
        ...state,
        pending: false,
        interviewerNotFound: true,
      };
    case 'GOT_CURRENT_USER':
      return {
        ...state,
        user: action.user,
        pending: false,
      };
    case 'GOT_INTERVIEWERS':
      return {
        ...state,
        interviewers: action.interviewers,
        pending: false,
      };
    case 'GOT_INTERVIEWS':
      return {
        ...state,
        interviews: action.interviews,
        pagination: action.pagination,
        pending: false,
        interviewStatusFilter: action.interviewStatusFilter,
        interviewScheduled: action.interviewScheduled,
        interviewReportPending: action.interviewReportPending,
        interviewCompleted: action.interviewCompleted,
        missingVideo: action.missingVideo,
        querySearch: action.querySearch,
        dateSearchStart: action.dateSearchStart,
        dateSearchEnd: action.dateSearchEnd,
        interviewerId: action.interviewerId,
        invalidInterview: false,
      };
    case 'GOT_INTERVIEW':
      return {
        ...state,
        interview: action.interview,
        pending: false,
        invalidInterview: false,
        interviewError: false,
      };
    // Only the interview request itself blocks this screen. `state.error` is also set by the
    // resume download, the checklist and the job-opening PDF, which leave the report usable.
    case 'INTERVIEW_FAILED':
      return {
        ...state,
        interviewError: true,
        error: action.error,
        pending: false,
      };
    case 'GOT_METADATA':
      return {
        ...state,
        metadata: action.metadata.metadata,
        experienceMetadata: action.metadata.internalInterviewExperienceMetadata,
        metadataError: false,
        pending: false,
      };
    // The report form cannot render without the form metadata, so this failure gets its own flag.
    // Gating the error screen on `state.error` alone would also catch the side requests that
    // dispatch FAILED on their own (the checklist, the resume download, the job-opening PDF) and
    // leave the report perfectly usable.
    case 'METADATA_FAILED':
      console.log(action.error);
      return {
        ...state,
        metadataError: true,
        error: action.error,
        pending: false,
      };
    case 'GOT_CANDIDATE':
      return {
        ...state,
        candidate: action.candidate,
        pending: false,
      };
    case 'GOT_JD_PDF':
      return {
        ...state,
        jdPDF: { id: action.id, url: action.url },
        pending: false,
      };
    case 'GOT_RESUME_PDF':
      return {
        ...state,
        resumePDF: { id: action.id, url: action.url },
        pending: false,
      };
    case 'FAILED':
      console.log(action.error);
      return {
        ...state,
        pending: false,
        error: action.error,
      };
    case 'UPDATED_TOKEN':
      return {
        ...state,
        user: action.user,
        pending: false,
      };
    case 'SETTED_INTERVIEW_SUBMIT_STATUS':
      return {
        ...state,
        interviewSubmitStatus: action.interviewSubmitStatus,
        interviewSubmitValidation: action.interviewSubmitValidation,
        pending: false,
      };
    case 'RESETTED_INTERVIEWS_FILTERS':
      return {
        ...state,
        interviewStatusFilter: 'All Interviews',
        interviewScheduled: false,
        interviewReportPending: false,
        interviewCompleted: false,
        missingVideo: false,
        querySearch: '',
        dateSearchStart: '',
        dateSearchEnd: '',
        pending: true,
      };
    case 'INVALID_INTERVIEW':
      return {
        ...state,
        invalidInterview: true,
        pending: false,
      };
    case 'SET_CHECKED':
      return {
        ...state,
        nonTechCheckbox: action.nonTechCheckbox,
      };

    case 'SET_TEMPORAL_LOCK_INTERVIEW':
      return {
        ...state,
        temporalLockInterviews: action.lockedInterviews,
      };

    case 'SET_TEMPORAL_UNLOCK_INTERVIEW':
      return {
        ...state,
        temporalLockInterviews: action.lockedInterviews,
      };
    case 'CLEAN_ERROR':
      return {
        ...state,
        error: '',
        metadataError: false,
        interviewError: false,
      };
    case 'CLEAN_INTERVIEW':
      return {
        ...state,
        interview: null,
        // A leftover error (a resume download that failed on the previous interview) would
        // otherwise turn the loading spinner into the error screen, which is gated on
        // `state.error && !state.interview`.
        error: '',
        invalidInterview: false,
      };
    case 'GOT_INTERVIEW_AND_UPDATE': {
      console.log(action.interviews);
      return {
        ...state,
        interviews: action.interviews,
        pending: false,
      };
    }
    case 'GOT_CHECKLIST': {
      return {
        ...state,
        checklistItems: action.checklistItems,
        // Every other GOT_* case clears this. Without it the flag stayed up after opening an
        // interview detail, which kept the list skeletons on, suppressed the empty state and
        // blocked the redirect to /interviews/404 (it is gated on `!state.pending`).
        pending: false,
      };
    }
    default:
      throw new Error('unknown action type');
  }
};

const CURRENT_USER_MAX_ATTEMPTS = 3;
const CURRENT_USER_RETRY_DELAY_MS = 750;

const wait = ms =>
  new Promise(resolve => {
    setTimeout(resolve, ms);
  });

// getByEmail powers the whole app: it populates state.user, which gates every other
// request. A transient failure here used to either leave state.user null (so the interviews request never fired) or
// crash on response.json() for an empty 500 body — both surfaced as an infinite spinner.
// Updated to verify response.ok and retry a few times
async function fetchCurrentUser(jwtToken, attempt = 1) {
  try {
    const response = await fetch(`${settings.BE_ENDPOINT}/interviewer/getByEmail`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'x-access-token': jwtToken,
      },
    });
    if (response.ok) return response;
    const httpError = new Error(`getByEmail responded with status ${response.status}`);
    httpError.statusCode = response.status;
    throw httpError;
  } catch (error) {
    // Client errors (e.g. 404 = no active/current interviewer row) are deterministic; retrying won't help
    // Only retry transient failures: network errors (no statusCode) and 5xx.
    const isRetryable = !error.statusCode || error.statusCode >= 500;
    if (isRetryable && attempt < CURRENT_USER_MAX_ATTEMPTS) {
      await wait(CURRENT_USER_RETRY_DELAY_MS * attempt);
      return fetchCurrentUser(jwtToken, attempt + 1);
    }
    throw error;
  }
}

const asyncActionHandlers = {
  GET_CURRENT_USER:
    ({ dispatch }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });

        const response = await fetchCurrentUser(action.jwtToken);
        const data = await response.json();

        const user = { ...data, token: action.jwtToken };
        dispatch({ type: 'GOT_CURRENT_USER', user });

        if (user?.impersonateAllowed) dispatch({ type: 'GET_INTERVIEWERS', jwtToken: action.jwtToken });
      } catch (error) {
        // 404 = no active/current interviewer row
        // Flag it for a tailored message instead of the generic error + "Try again".
        if (error?.statusCode === 404) {
          dispatch({ type: 'USER_NOT_FOUND' });
        } else {
          dispatch({ type: 'FAILED', error });
        }
      }
    },

  GET_INTERVIEWERS:
    ({ dispatch }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });

        const response = await fetch(`${settings.BE_ENDPOINT}/interviewer/getInterviewers`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': action.jwtToken,
          },
        });
        const interviewers = await response.json();
        dispatch({ type: 'GOT_INTERVIEWERS', interviewers });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },

  GET_INTERVIEW_AND_STORE:
    ({ dispatch }) =>
    async action => {
      try {
        // Drop the interview loaded before this read. The report form is seeded once, by an effect
        // keyed on the interview id, so reopening the same interview kept the pre-submit values:
        // the id never changed, the effect never re-ran, and only a page reload cleared the store.
        dispatch({ type: 'CLEAN_INTERVIEW' });
        dispatch({ type: 'STARTED' });
        const response = await fetch(`${settings.BE_ENDPOINT}/interview/getByZohoId/${action.interviewId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': action.token,
          },
        });
        if (response.status === 200) {
          const interview = await response.json();

          if (interview.interviewData.id === action.interviewId) {
            dispatch({ type: 'GOT_INTERVIEW', interview });
          }
        } else {
          dispatch({ type: 'INVALID_INTERVIEW' });
        }
      } catch (error) {
        dispatch({ type: 'INTERVIEW_FAILED', error });
      }
    },

  GET_METADATA:
    ({ dispatch, _getState }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        const response = await fetch(`${settings.BE_ENDPOINT}/interview/getMetadataForm`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': action.token,
          },
        });
        if (!response.ok) {
          throw new Error(`getMetadataForm responded with status ${response.status}`);
        }
        const metadata = await response.json();
        // When the API cannot reach Zoho it still answers 200, with a Fail DTO carrying no field
        // lists. Reducing that left both lists undefined and the report form spinning forever with
        // nothing to retry, so treat it as the failure it is.
        if (!metadata?.metadata || !metadata?.internalInterviewExperienceMetadata) {
          throw new Error(metadata?.message || 'getMetadataForm returned no form metadata');
        }
        dispatch({ type: 'GOT_METADATA', metadata });
      } catch (error) {
        dispatch({ type: 'METADATA_FAILED', error });
      }
    },

  GET_CANDIDATE:
    ({ dispatch, _getState }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        const response = await fetch(`${settings.BE_ENDPOINT}/candidate/getById/${action.candidateId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': action.token,
          },
        });
        const candidate = await response.json();
        dispatch({ type: 'GOT_CANDIDATE', candidate });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },

  GET_JD_PDF:
    ({ dispatch }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        const response = await fetch(
          `${settings.BE_ENDPOINT}/jobOpening/summaryDownload/${action.interviewId}`,

          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/pdf',
              'x-access-token': action.token,
            },
          },
        );

        if (response.status !== 200 || response.statusText === 'Not Found') {
          throw new Error('PDF Not Found');
        }

        const jdPDF = await response.blob();
        dispatch({ type: 'GOT_JD_PDF', id: action.interviewId, url: jdPDF });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },
  GET_RESUME_PDF:
    ({ dispatch }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });

        const response = await fetch(
          `${settings.BE_ENDPOINT}/candidate/resumeDownload/${action.candidateId}`,

          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/pdf',
              'x-access-token': action.token,
            },
          },
        );

        // Plenty of candidates simply have no resume stored in Zoho. That is an absent document,
        // not a failure: reporting it as one put the whole page into an error state.
        if (response.status !== 200 || response.statusText === 'Not Found') {
          dispatch({ type: 'GOT_RESUME_PDF', id: action.candidateId, url: null });
          return;
        }

        const resumePDF = await response.blob();
        dispatch({ type: 'GOT_RESUME_PDF', id: action.candidateId, url: resumePDF });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },
  UPDATE_TOKEN_AND_POST_INTERVIEW:
    ({ dispatch, getState }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        const userResponse = await currentUser();
        const userData = await userResponse;

        const { user } = getState();
        user.token = userData.signInUserSession.accessToken.jwtToken;

        const response = await fetch(`${settings.BE_ENDPOINT}/interview`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': user.token,
          },
          body: JSON.stringify({
            ...action.submitInterview,
          }),
        });

        const interviewSubmitStatus = await response.json();
        if (interviewSubmitStatus.status === 'Fail Validation') {
          dispatch({
            type: 'SET_INTERVIEW_SUBMIT_STATUS',
            interviewSubmitStatus: interviewSubmitStatus.status,
            interviewSubmitValidation: interviewSubmitStatus.message,
          });
        } else {
          const state = getState();
          setAmplitudeUserId(state.user.email);
          sendAmplitudeData('SUBMIT_REPORT', {
            Interviewer: state.interview.interviewData.Interviewer.name,
            Candidate: state.interview.interviewData.Candidate_Name.name,
            submitType: state.interview.interviewData.Report_Delivered_Time ? 'Edit' : 'New',
            interviewDate: state.interview.interviewData.Interview_Date,
            interviewStatus: state.interview.interviewData.Interview_Status,
          });
          dispatch({
            type: 'UPDATED_TOKEN',
            user,
          });
          dispatch({
            type: 'SET_INTERVIEW_SUBMIT_STATUS',
            interviewSubmitStatus: interviewSubmitStatus.message,
          });
        }
      } catch (error) {
        dispatch({ type: 'FAILED', error });
        dispatch({
          type: 'SET_INTERVIEW_SUBMIT_STATUS',
          interviewSubmitStatus: error.message,
        });
      }
    },

  SET_INTERVIEW_SUBMIT_STATUS:
    ({ dispatch }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        dispatch({
          type: 'SETTED_INTERVIEW_SUBMIT_STATUS',
          interviewSubmitStatus: action.interviewSubmitStatus,
          interviewSubmitValidation: action.interviewSubmitValidation,
        });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },

  GET_INTERVIEWS_PAGINATION_PARAMS:
    ({ dispatch, getState }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        const requestUrl = new URL(`${settings.BE_ENDPOINT}/interview/getAllByScreener2`);

        requestUrl.searchParams.append('page', action.page);
        requestUrl.searchParams.append('limit', 10);

        const state = getState();
        const dateSearchStartParam = Object.prototype.hasOwnProperty.call(action, 'dateSearchStart')
          ? action.dateSearchStart
          : state.dateSearchStart || '';
        const dateSearchEndParam = Object.prototype.hasOwnProperty.call(action, 'dateSearchEnd')
          ? action.dateSearchEnd
          : state.dateSearchEnd || '';

        if (
          action.interviewScheduled ||
          action.interviewReportPending ||
          action.interviewCompleted ||
          action.missingVideo ||
          action.querySearch
        ) {
          if (Object.prototype.hasOwnProperty.call(action, 'interviewScheduled'))
            requestUrl.searchParams.append('interviewScheduled', action.interviewScheduled);

          if (Object.prototype.hasOwnProperty.call(action, 'interviewReportPending'))
            requestUrl.searchParams.append('interviewReportPending', action.interviewReportPending);

          if (Object.prototype.hasOwnProperty.call(action, 'interviewCompleted'))
            requestUrl.searchParams.append('interviewCompleted', action.interviewCompleted);

          if (Object.prototype.hasOwnProperty.call(action, 'missingVideo'))
            requestUrl.searchParams.append('missingVideo', action.missingVideo);

          if (Object.prototype.hasOwnProperty.call(action, 'querySearch') && action.querySearch.length > 0)
            requestUrl.searchParams.append('querySearch', action.querySearch);
        } else if (action.interviewStatusFilter) {
          switch (action.interviewStatusFilter) {
            case 'All Interviews':
              requestUrl.searchParams.append('interviewAll', true);
              break;

            case 'Scheduled':
              requestUrl.searchParams.append('interviewScheduled', true);
              break;

            case 'Report Pending':
              requestUrl.searchParams.append('interviewReportPending', true);
              break;

            case 'Missing Video':
              requestUrl.searchParams.append('missingVideo', true);
              break;

            case 'Completed':
              requestUrl.searchParams.append('interviewCompleted', true);
              break;

            default:
              break;
          }
        }

        if (dateSearchStartParam && dateSearchStartParam.length > 0) {
          requestUrl.searchParams.append('dateSearchStart', dateSearchStartParam);
        } else {
          requestUrl.searchParams.append('dateSearchStart', new Date('2017-01-01').toISOString().slice(0, 10));
        }

        if (dateSearchEndParam && dateSearchEndParam.length > 0) {
          requestUrl.searchParams.append('dateSearchEnd', dateSearchEndParam);
        } else {
          const currentDate = new Date();
          currentDate.setMonth(currentDate.getMonth() + 3);
          const next3MonthsDate = currentDate.toISOString().slice(0, 10);
          requestUrl.searchParams.append('dateSearchEnd', next3MonthsDate);
        }

        const getInterviewerId = Object.prototype.hasOwnProperty.call(action, 'interviewerId')
          ? action.interviewerId
          : getState().interviewerId;

        if (getInterviewerId) requestUrl.searchParams.append('interviewerId', getInterviewerId);

        const response = await fetch(requestUrl, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': action.token,
          },
        });
        const paginationData = await response.json();
        const interviews = paginationData?.docs;

        const pagination = {
          page: paginationData?.page,
          count: paginationData?.count,
          totalPages: paginationData?.totalPages,
        };

        dispatch({
          type: 'GOT_INTERVIEWS',
          interviews,
          pagination,
          interviewStatusFilter: action.interviewStatusFilter || 'All Interviews',
          interviewScheduled: action.interviewScheduled || false,
          interviewReportPending: action.interviewReportPending || false,
          missingVideo: action.missingVideo || false,
          interviewCompleted: action.interviewCompleted || false,
          querySearch: action.querySearch || '',
          dateSearchStart: dateSearchStartParam || '',
          dateSearchEnd: dateSearchEndParam || '',
          interviewerId: getInterviewerId,
        });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },

  RESET_INTERVIEWS_FILTERS:
    ({ dispatch }) =>
    async () => {
      try {
        dispatch({ type: 'STARTED' });
        dispatch({
          type: 'RESETTED_INTERVIEWS_FILTERS',
        });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },

  TEMPORAL_LOCK_INTERVIEW:
    ({ dispatch, getState }) =>
    async action => {
      try {
        const state = getState();
        const { temporalLockInterviews } = state;
        const updatedLockedInterviews = [...temporalLockInterviews];
        const lockTime = new Date();
        updatedLockedInterviews.push({
          id: action.interviewId,
          lockTime: lockTime.setSeconds(lockTime.getSeconds() + 70),
        });
        dispatch({
          type: 'SET_TEMPORAL_LOCK_INTERVIEW',
          lockedInterviews: updatedLockedInterviews,
        });

        setTimeout(() => {
          dispatch({
            type: 'TEMPORAL_UNLOCK_INTERVIEW',
            interviewId: action.interviewId,
          });
        }, 70000);
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },
  TEMPORAL_UNLOCK_INTERVIEW:
    ({ dispatch, getState }) =>
    async action => {
      try {
        const state = getState();
        const { temporalLockInterviews } = state;
        const updatedLockedInterviews = [...temporalLockInterviews].filter(item => item.id !== action.interviewId);
        dispatch({
          type: 'SET_TEMPORAL_UNLOCK_INTERVIEW',
          lockedInterviews: updatedLockedInterviews,
        });
        dispatch({
          type: 'GET_INTERVIEW_AND_UPDATE_INTERVIEW_STATUS',
          token: state.user.token,
          interviewId: action.interviewId,
        });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },
  GET_INTERVIEW_AND_UPDATE_INTERVIEW_STATUS:
    ({ dispatch, getState }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        const response = await fetch(`${settings.BE_ENDPOINT}/interview/getByZohoId/${action.interviewId}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'x-access-token': action.token,
          },
        });
        if (response.status === 200) {
          const interview = await response.json();

          if (interview.interviewData.id === action.interviewId) {
            const state = getState();
            const { interviews } = state;
            const updateInterview = interviews.find(item => item.interviewData.id === action.interviewId);
            updateInterview.interviewData.Interview_Status = interview.interviewData.Interview_Status;

            dispatch({ type: 'GOT_INTERVIEW_AND_UPDATE', interviews });
          }
        }
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },
  GET_CHECKLIST:
    ({ dispatch }) =>
    async action => {
      try {
        dispatch({ type: 'STARTED' });
        const response = await fetch(
          `${settings.RESUME_BE_ENDPOINT}/resume-automation/checklist/${action.jobOpeningId}`,
          {
            method: 'GET',
          },
        );
        // A gateway page or a wrong endpoint answers HTML, and parsing it blindly surfaced an
        // opaque "Unexpected token '<'" as the screen's error. Read the body once and decide.
        const body = await response.text();
        let checklistItemsJson;
        try {
          checklistItemsJson = JSON.parse(body);
        } catch {
          throw new Error(`getChecklist responded with a non-JSON body (status ${response.status})`);
        }
        if (!response.ok && checklistItemsJson?.message !== 'CheckList not found') {
          throw new Error(checklistItemsJson?.message || `getChecklist responded with status ${response.status}`);
        }
        let checklistItems;
        if (checklistItemsJson.message === 'CheckList not found') {
          checklistItems = {
            id: null,
            checks: [{ text: 'Test', weight: 1, required: false, internal: true, checked: false, id: '1' }],
            JobOpeningId: '',
            updatedAt: '.',
          };
        } else {
          checklistItems = checklistItemsJson;
        }
        dispatch({ type: 'GOT_CHECKLIST', checklistItems });
      } catch (error) {
        dispatch({ type: 'FAILED', error });
      }
    },
};

const useValue = () => useReducerAsync(reducer, initialState, asyncActionHandlers);

export const { Provider, useTrackedState, useUpdate: useDispatch } = createContainer(useValue);
